/**
 * Container.js — v5
 *
 * Cambios:
 *  1. Nueva prop `className`: aplica el className original del HTML al elemento DOM.
 *     Esto permite que las reglas CSS del StylesBlock (ej: .card-channeler,
 *     .card-meta-bottom, .grid) se apliquen correctamente.
 *
 *  2. Se removió `width: '100%'` del style override.
 *     Antes, todos los containers tenían width: 100% forzado, lo que causaba
 *     que elementos que debían ser auto-width (como .card-channeler en un
 *     flex-end parent) se expandieran a todo el ancho y parecieran ir a la
 *     izquierda en vez de la derecha.
 *     Ahora el ancho lo controla el CSS class (via StylesBlock) y el flujo
 *     natural de flex/grid/block.
 */
import React from 'react';
import { useNode } from '@craftjs/core';
import { TextField, FormControl, FormLabel, Typography, Box, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as Icons from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  // Outline solo en el editor para drag&drop — no interfiere con el layout
  container: {
    outline: '1px dashed transparent',
    cursor: 'pointer',
    transition: 'outline-color 0.15s',
    '&:hover': { outlineColor: '#2196f3' },
  },
  settings: { padding: theme.spacing(2) },
  rawStyleTextarea: {
    width: '100%',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.68rem',
    padding: theme.spacing(1),
    border: '1px solid #ccc',
    borderRadius: 4,
    minHeight: 80,
    resize: 'vertical',
    outline: 'none',
    lineHeight: 1.5,
    color: '#333',
    backgroundColor: '#fafafa',
  },
  hint: { fontSize: '0.72rem', color: '#999', marginTop: 4, marginBottom: theme.spacing(1) },
  badge: {
    display: 'inline-block',
    borderRadius: 3,
    padding: '2px 8px',
    fontSize: '0.72rem',
    fontFamily: 'monospace',
    fontWeight: 700,
    marginRight: 4,
  },
  gridBadge:     { backgroundColor: '#e3f2fd', color: '#1565c0' },
  gradientBadge: { backgroundColor: '#fce4ec', color: '#b71c1c' },
  classBadge:    { backgroundColor: '#f3e5f5', color: '#6a1b9a' },
  gradientPreview: {
    height: 32, borderRadius: 4, border: '1px solid #e0e0e0', marginBottom: theme.spacing(1),
  },
  clearBtn: {
    fontSize: '0.72rem', padding: '2px 8px', minHeight: 'auto',
    color: '#f44336', borderColor: '#f44336', marginTop: theme.spacing(0.5),
  },
}));

function toHexSafe(color) {
  if (!color || color === 'transparent') return '#ffffff';
  if (typeof color !== 'string') return '#ffffff';
  if (color.startsWith('#')) return color.length === 4 || color.length === 7 ? color : '#ffffff';
  const m = color.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/);
  if (m) {
    const h = (n) => parseInt(n).toString(16).padStart(2, '0');
    return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
  }
  return '#ffffff';
}

function hasAlpha(color) {
  if (!color || typeof color !== 'string') return false;
  const m = color.match(/rgba\(\s*\d+,\s*\d+,\s*\d+,\s*([\d.]+)\s*\)/);
  return m ? parseFloat(m[1]) > 0 && parseFloat(m[1]) < 1 : false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────────────
export const Container = ({
  children,
  backgroundColor,
  backgroundImage,
  padding,
  flexDirection,
  tagName,
  className,
  rawStyle,
}) => {
  const classes    = useStyles();
  const { connectors: { connect, drag } } = useNode();

  const isGrid     = rawStyle?.display === 'grid';
  const hasGradient = backgroundImage && backgroundImage !== '' && backgroundImage !== 'none';

  const style = {
    // 1. rawStyle como base (preserva grid, gap, margin, border-radius, etc.)
    ...rawStyle,

    // 2. Gradiente explícito
    ...(hasGradient ? { backgroundImage } : {}),

    // 3. Color de fondo explícito (sobreescribe rawStyle)
    ...(backgroundColor && backgroundColor !== 'transparent'
      ? { backgroundColor }
      : {}),

    // 4. Padding explícito (solo si fue editado)
    ...(padding ? { padding: `${padding}px` } : {}),

    // 5. Layout flex (no tocar si es grid)
    ...(!isGrid
      ? { display: 'flex', flexDirection: flexDirection || rawStyle?.flexDirection || 'column' }
      : {}),

    // NOTA: width: '100%' fue removido intencionalmente.
    // El ancho lo controla:
    //  - Las reglas CSS del StylesBlock vía className (para HTML importado)
    //  - El flujo natural de block/flex/grid (para componentes creados desde toolbox)
    // Forzar width: 100% rompía la alineación flex-end (ej: .card-channeler)
  };

  const VALID_TAGS = ['div', 'ul', 'ol', 'nav', 'header', 'footer', 'main', 'article', 'aside', 'section'];
  const Tag = VALID_TAGS.includes(tagName) ? tagName : 'div';

  // Combinar clase del editor (outline hover) con clase original del HTML
  const combinedClass = [classes.container, className].filter(Boolean).join(' ');

  return (
    <Tag
      ref={(ref) => connect(drag(ref))}
      className={combinedClass}
      style={style}
    >
      {children}
    </Tag>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const ContainerSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    backgroundColor,
    backgroundImage,
    padding,
    flexDirection,
    className,
    rawStyle,
  } = useNode((node) => ({
    backgroundColor: node.data.props.backgroundColor,
    backgroundImage: node.data.props.backgroundImage,
    padding:         node.data.props.padding,
    flexDirection:   node.data.props.flexDirection,
    className:       node.data.props.className,
    rawStyle:        node.data.props.rawStyle,
  }));

  const isGrid      = rawStyle?.display === 'grid';
  const hasGradient = backgroundImage && backgroundImage !== '' && backgroundImage !== 'none';
  const isRgba      = hasAlpha(backgroundColor);
  const rawStyleStr = rawStyle ? JSON.stringify(rawStyle, null, 2) : '{}';

  const handleColorChange = (newColor) => {
    setProp((props) => {
      props.backgroundColor = newColor;
      if (props.backgroundImage) props.backgroundImage = '';
    });
  };

  return (
    <div className={classes.settings}>

      {/* Badges */}
      <Box mb={1} display="flex" style={{ gap: 6, flexWrap: 'wrap' }}>
        {isGrid      && <span className={`${classes.badge} ${classes.gridBadge}`}>grid</span>}
        {hasGradient && <span className={`${classes.badge} ${classes.gradientBadge}`}>gradiente</span>}
        {className   && <span className={`${classes.badge} ${classes.classBadge}`}>.{className.split(' ')[0]}</span>}
      </Box>

      {/* Clase HTML */}
      {className && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Clase CSS original</FormLabel>
          <TextField
            size="small"
            value={className}
            onChange={(e) => setProp((props) => (props.className = e.target.value))}
            inputProps={{ style: { fontSize: '0.78rem', fontFamily: 'monospace' } }}
          />
        </FormControl>
      )}

      {/* Gradiente */}
      {hasGradient && (
        <Box mb={2}>
          <FormLabel style={{ fontSize: '0.78rem' }}>Fondo con gradiente</FormLabel>
          <div className={classes.gradientPreview} style={{ background: backgroundImage, marginTop: 6 }} />
          <TextField
            fullWidth size="small" variant="outlined"
            value={backgroundImage}
            onChange={(e) => setProp((props) => (props.backgroundImage = e.target.value))}
            inputProps={{ style: { fontSize: '0.72rem', fontFamily: 'monospace' } }}
          />
          <Button
            variant="outlined" size="small" className={classes.clearBtn}
            startIcon={<Icons.Delete style={{ fontSize: 14 }} />}
            onClick={() => setProp((props) => (props.backgroundImage = ''))}
          >
            Quitar gradiente
          </Button>
        </Box>
      )}

      {/* Color de fondo */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Color de fondo {isRgba ? '(semitransparente)' : ''}</FormLabel>
        {isRgba && (
          <Typography className={classes.hint}>
            Tiene transparencia ({backgroundColor}). El picker muestra el color base.
          </Typography>
        )}
        <TextField type="color" value={toHexSafe(backgroundColor)} onChange={(e) => handleColorChange(e.target.value)} />
        <TextField
          size="small" variant="outlined"
          value={backgroundColor || ''}
          onChange={(e) => setProp((props) => (props.backgroundColor = e.target.value))}
          placeholder="rgba(255,255,255,0.05) o #424a4a"
          inputProps={{ style: { fontSize: '0.72rem', fontFamily: 'monospace' } }}
          style={{ marginTop: 6 }}
        />
      </FormControl>

      {/* Padding */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Padding (px)</FormLabel>
        <TextField type="number" value={padding || 0}
          onChange={(e) => setProp((props) => (props.padding = Number(e.target.value)))} />
      </FormControl>

      {/* Dirección flex */}
      {!isGrid && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Dirección</FormLabel>
          <TextField select value={flexDirection || 'column'}
            onChange={(e) => setProp((props) => (props.flexDirection = e.target.value))}
            SelectProps={{ native: true }}
          >
            <option value="column">Vertical</option>
            <option value="row">Horizontal</option>
            <option value="row-reverse">Horizontal invertido</option>
            <option value="column-reverse">Vertical invertido</option>
          </TextField>
        </FormControl>
      )}

      {/* rawStyle */}
      {rawStyle && Object.keys(rawStyle).length > 0 && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Estilos heredados (CSS)</FormLabel>
          <Typography className={classes.hint}>Las props de arriba tienen prioridad.</Typography>
          <textarea
            className={classes.rawStyleTextarea}
            defaultValue={rawStyleStr}
            onBlur={(e) => {
              try { setProp((props) => (props.rawStyle = JSON.parse(e.target.value))); }
              catch (_) {}
            }}
            spellCheck={false}
            style={{ marginTop: 8 }}
          />
        </FormControl>
      )}
    </div>
  );
};

Container.craft = {
  props: {
    backgroundColor: 'transparent',
    backgroundImage: '',
    padding:         20,
    flexDirection:   'column',
    tagName:         'div',
    className:       '',
    rawStyle:        {},
  },
  related: { settings: ContainerSettings },
};